/*
  Umami custom events for moonlightmounts.com.

  Bridges the theme's PubSub events (add to cart, variant change, cart errors, cart
  quantity changes) and a few DOM interactions (product accordions, express checkout on
  product pages and the cart page, the GovX verify button, tel:/sms: links)
  into Umami custom events so funnels, journeys and the umami-review skill can see
  what happens between the product page and Shopify's off-site checkout.

  Click-type events (the three "Check out" buttons) use Umami's own auto-track
  attribute (`data-umami-event`) in the Liquid instead of this file.

  Event names and properties are documented in .claude/rules/kb/tools/umami.md. Keep them
  stable: every rename breaks the funnels and the review script that read them.

  Loaded with `defer` after pubsub.js, constants.js and the Umami tracker, so
  `subscribe`, `PUB_SUB_EVENTS` and `window.umami` exist by the time this runs.
  Every call is guarded so a blocked tracker never breaks the storefront.
*/
(function () {
  'use strict';

  var MAX_TEXT = 120;

  function track(name, data) {
    try {
      if (window.umami && typeof window.umami.track === 'function') {
        window.umami.track(name, data);
      }
    } catch (e) {
      /* never let analytics break the store */
    }
  }

  function productHandle() {
    var match = window.location.pathname.match(/\/products\/([^/?#]+)/);
    return match ? decodeURIComponent(match[1]) : undefined;
  }

  function clip(value) {
    if (value === undefined || value === null) return undefined;
    var text = String(value).replace(/\s+/g, ' ').trim();
    return text.length > MAX_TEXT ? text.slice(0, MAX_TEXT) : text;
  }

  function dollars(cents) {
    var n = Number(cents);
    return isFinite(n) ? Math.round(n) / 100 : undefined;
  }

  function compact(data) {
    var out = {};
    Object.keys(data).forEach(function (key) {
      if (data[key] !== undefined && data[key] !== null && data[key] !== '') out[key] = data[key];
    });
    return out;
  }

  /* ---- PubSub-driven events ------------------------------------------------ */

  function onCartUpdate(event) {
    if (!event) return;
    var cart = event.cartData || {};

    if (event.source === 'product-form' || event.source === 'quick-add') {
      // /cart/add.js returns the added line item (handle, titles, price in cents).
      // quick-add-bulk returns the whole cart instead, so fall back to the matching line.
      var line = cart;
      if (!cart.handle && Array.isArray(cart.items)) {
        var wanted = String(event.productVariantId || event.variantId || '');
        line = cart.items.filter(function (item) { return String(item.variant_id || item.id) === wanted; })[0] || {};
      }
      track('add_to_cart', compact({
        product: line.handle || productHandle(),
        variant: clip(line.variant_title),
        variant_id: String(line.variant_id || line.id || event.productVariantId || ''),
        price: dollars(line.price),
        qty: line.quantity,
        source: event.source,
      }));
      return;
    }

    if (event.source === 'cart-items') {
      // Quantity changed or a line removed from the cart page / drawer.
      var items = Array.isArray(cart.items) ? cart.items : [];
      var still = items.filter(function (item) { return String(item.variant_id || item.id) === String(event.variantId || ''); })[0];
      track(still ? 'cart_quantity_change' : 'remove_from_cart', compact({
        variant_id: String(event.variantId || ''),
        product: still ? still.handle : undefined,
        qty: still ? still.quantity : 0,
        cart_items: cart.item_count,
      }));
    }
  }

  function onCartError(event) {
    if (!event) return;
    track('cart_error', compact({
      product: productHandle(),
      variant_id: String(event.productVariantId || ''),
      message: clip(event.message || event.errors),
      source: event.source,
    }));
  }

  var lastVariantId;
  function onVariantChange(event) {
    var variant = event && event.data && event.data.variant;
    if (!variant || !variant.id) return;
    if (String(variant.id) === lastVariantId) return; // Dawn republishes on quantity/URL updates
    lastVariantId = String(variant.id);
    track('variant_change', compact({
      product: productHandle(),
      variant: clip(variant.title),
      variant_id: lastVariantId,
      available: variant.available ? 'yes' : 'no',
      price: dollars(variant.price),
    }));
  }

  if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
    subscribe(PUB_SUB_EVENTS.cartUpdate, onCartUpdate);
    subscribe(PUB_SUB_EVENTS.cartError, onCartError);
    subscribe(PUB_SUB_EVENTS.variantChange, onVariantChange);
  }

  /* ---- DOM-driven events ---------------------------------------------------- */

  // Product-page accordions (Installation, Guides & Resources, FAQ, Guarantee, ...).
  // `toggle` doesn't bubble, so listen in the capture phase; only count opens.
  document.addEventListener('toggle', function (e) {
    var details = e.target;
    if (!details || details.tagName !== 'DETAILS' || !details.open) return;
    if (!details.closest('.product__accordion')) return;
    var heading = details.querySelector('summary .accordion__title, summary h2, summary');
    track('accordion_open', compact({
      title: clip(heading && heading.textContent),
      product: productHandle(),
    }));
  }, true);

  // Dynamic checkout (Shop Pay / PayPal / "Buy it now") on the product page. Shopify
  // renders those buttons, so they can't carry a data-umami-event attribute.
  // The GovX ID app styles its "Save up to 15%" button with the same classes and wraps it
  // in a .shopify-payment-button div, so GovX clicks are split out first as govx_click
  // (2026-11-02; until then they counted as express_checkout). express_checkout only
  // counts buttons inside the theme's <product-form>.
  document.addEventListener('click', function (e) {
    var target = e.target;
    if (!target || typeof target.closest !== 'function') return;
    if (target.closest('[data-govx-href], [class*="govx"]')) {
      var path = window.location.pathname;
      track('govx_click', compact({
        location: clip(path.indexOf('/pages/') === 0 ? path.slice(7) : path),
        product: productHandle(),
      }));
      return;
    }
    if (!target.closest('.shopify-payment-button')) return;
    if (!target.closest('product-form')) return;
    track('express_checkout', compact({ product: productHandle() }));
  }, true);

  // Wallet buttons on the cart page (PayPal, Shop Pay, ... from
  // content_for_additional_checkout_buttons, 2026-10-17). They skip the "Check out" button,
  // so begin_checkout never sees them, and express_checkout is product-page only. `wallet`
  // is the clicked button's element name without the shopify- prefix and -button suffix.
  document.addEventListener('click', function (e) {
    var target = e.target;
    if (!target || typeof target.closest !== 'function') return;
    var area = target.closest('.additional-checkout-buttons');
    if (!area) return;
    var wallet;
    for (var el = target; el && el !== area; el = el.parentElement) {
      var tag = el.tagName ? el.tagName.toLowerCase() : '';
      if (tag.indexOf('-') !== -1 || tag === 'button') {
        wallet = tag.replace(/^shopify-/, '').replace(/-button$/, '');
        break;
      }
    }
    if (!wallet) return; // a click on the gap between buttons
    track('cart_express_checkout', compact({ location: 'cart-page', wallet: clip(wallet) }));
  }, true);

  // Tap-to-call and tap-to-text links (2026-10-16 / 2026-10-17): calls reach an AI voice
  // assistant and texts an AI text assistant. One listener covers every tel: link
  // (phone_click) and sms: link (sms_click) on the site (product call note, contact page, FAQ,
  // whitepaper, footer), including the ones inside admin rich text that can't carry attributes.
  document.addEventListener('click', function (e) {
    var target = e.target;
    if (!target || typeof target.closest !== 'function') return;
    var link = target.closest('a[href^="tel:"], a[href^="sms:"]');
    if (!link) return;
    var path = window.location.pathname;
    var where = link.closest('footer') ? 'footer'
      : link.closest('.product__call-note') ? 'product'
      : path.indexOf('/pages/') === 0 ? path.slice(7)
      : path;
    var name = link.getAttribute('href').indexOf('sms:') === 0 ? 'sms_click' : 'phone_click';
    track(name, compact({ location: clip(where), product: productHandle() }));
  }, true);
})();
